import React, { useEffect, useState } from "react"
import ModalComponent from "../../components/Modal"
import {
  ButtonGroup,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner,
  ToggleButton,
} from "react-bootstrap"
import CustomSelect from "../../components/CustomSelect"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import * as Yup from "yup"
import axiosProvider from "../../lib/AxiosProvider"
import { admin } from "../../config/apiEndpoints"
import { handleOnchange, handleOnBlur } from "../../lib/GlobalMethods"
import { _firmwareVersionRegex_, _numberRegex_ } from "../../lib/Regex"

const AddEditCharger = ({
  handleClose,
  show,
  setShow,
  fetchData,
  setData,
  setIsUpdate,
  isUpdate,
}) => {
  const [copied, setCopied] = useState(false)
  const [selectedCharger, setSelectedCharger] = useState("single")
  const [selectedSingleconnector, setSelectedSingleconnector] = useState(null)
  const [loading, setLoading] = useState(false)
  const [connectorData, setConnectorData] = useState([])

  const validationSchema = Yup.object().shape({
    model_name: Yup.string()
      .min(2, "Enter at least 2 characters for model name")
      .required("Model name is required"),
    sn_number: Yup.string().required("SN Number is required"),
    vendor: Yup.string().required("vendor is required"),
    firmware_version: Yup.string().required("Firmware Version is required"),
    no_of_connector: Yup.string().required("No of connector is required"),
    min_output_current: Yup.string()
      .required("Min output current is required")
      .test(
        "min-max-current",
        "Min output current should not be greater than max output current",
        function (value) {
          const { max_output_current } = this.parent
          if (!value || !max_output_current) return true
          return parseFloat(value) <= parseFloat(max_output_current)
        }
      ),
    max_output_current: Yup.string().required("Max output current is required"),
    min_output_voltage: Yup.string()
      .required("Min output voltage is required")
      .test(
        "min-max-voltage",
        "Min output voltage should not be greater than max output voltage",
        function (value) {
          const { max_output_voltage } = this.parent
          if (!value || !max_output_voltage) return true
          return parseFloat(value) <= parseFloat(max_output_voltage)
        }
      ),
    max_output_voltage: Yup.string().required("Max output voltage is required"),
    power_rating: Yup.string().required("Power rating is required"),
    connector_type: Yup.mixed().when("no_of_connector", {
      is: "single",
      then: () =>
        Yup.mixed().when("update", {
          is: false,
          then: () => Yup.object().required("Connector type is required"),
          otherwise: () => Yup.object().required("Connector type is required"),
        }),
      otherwise: () => Yup.mixed().notRequired(),
    }),
    left_connector_type: Yup.mixed().when("no_of_connector", {
      is: "double",
      then: () =>
        Yup.mixed().when("update", {
          is: true,
          then: () => Yup.array().required("Left connector is required"),
          otherwise: () => Yup.object().required("Left connector is required"),
        }),
      otherwise: () => Yup.mixed().notRequired(),
    }),

    right_connector_type: Yup.mixed().when("no_of_connector", {
      is: "double",
      then: () =>
        Yup.mixed().when("update", {
          is: true,
          then: () => Yup.array().required("Right connector is required"),
          otherwise: () => Yup.object().required("Right connector is required"),
        }),
      otherwise: () => Yup.mixed().notRequired(),
    }),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      values = {
        model_name: values?.model_name,
        id: values?.sn_number,
        vendor: values?.vendor,
        firmware_version: values?.firmware_version,
        power_rating: values?.power_rating,
        no_of_connector: values?.no_of_connector,
        touch_screen: "true",
        output_voltage:
          values?.min_output_voltage + "-" + values?.max_output_voltage,
        output_current:
          values?.min_output_current + "-" + values?.max_output_current,
        connectors:
          values?.no_of_connector === "double"
            ? [
                {
                  id: values?.left_connector_type?.value,
                  position: "left",
                },
                {
                  id: values?.right_connector_type?.value,
                  position: "right",
                },
              ]
            : [
                {
                  id: values?.connector_type?.value,
                  position: "single",
                },
              ],
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin?.updateCharger + isUpdate.id
          : admin?.createCharger,
        data: values,
      })
      if (response?.data?.code === 201 || response?.data?.code === 200) {
        toast.success(response?.data?.message)
        setLoading(false)
        isUpdate
          ? setData((prevData) =>
              prevData.map((item) =>
                item.id === isUpdate.id
                  ? {
                      ...item,
                      ...response.data.data,
                    }
                  : item
              )
            )
          : fetchData("")
        setShow(false)
        handleClose()
        setIsUpdate(false)
        resetForm()
      } else {
        setLoading(false)
        throw { message: response?.response?.data?.message }
      }
    } catch (errors) {
      setLoading(false)
      toast.error(errors?.message)
    }
  }

  const fetchConnectorData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin?.connectorList,
      })
      const data = response?.data?.data?.map(({ id, connector_type }) => ({
        value: id,
        label: connector_type,
      }))
      setConnectorData(data)
    } catch (error) {
      toast.error(error)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          model_name: isUpdate?.model_name,
          sn_number: isUpdate?.id,
          vendor: isUpdate?.vendor,
          firmware_version: isUpdate?.firmware_version,
          no_of_connector: isUpdate?.no_of_connector,
          power_rating: isUpdate?.power_rating,
          connector_type: isUpdate?.connectors?.length
            ? {
                label: isUpdate?.connectors[0]?.connector_type,
                value: isUpdate?.connectors[0]?.id,
              }
            : [],
          min_output_current: isUpdate?.output_current?.split("-")[0],
          max_output_current: isUpdate?.output_current?.split("-")[1],
          min_output_voltage: isUpdate?.output_voltage?.split("-")[0],
          max_output_voltage: isUpdate?.output_voltage?.split("-")[1],
          left_connector_type:
            isUpdate?.no_of_connector === "double"
              ? {
                  label: isUpdate?.connectors[0]?.connector_type,
                  value: isUpdate?.connectors[0]?.id,
                }
              : {},
          right_connector_type:
            isUpdate?.no_of_connector === "double"
              ? {
                  label: isUpdate?.connectors[1]?.connector_type,
                  value: isUpdate?.connectors[1]?.id,
                }
              : {},
        }
      : {
          model_name: "",
          sn_number: "",
          vendor: "",
          firmware_version: "",
          no_of_connector: "single",
          power_rating: "",
          connector_type: "",
          left_connector_type: "",
          right_connector_type: "",
          min_output_current: "",
          max_output_current: "",
          min_output_voltage: "",
          max_output_voltage: "",
          update: isUpdate,
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, setFieldValue, resetForm, initialValues } =
    formik

  const handleSelect = (value) => {
    setSelectedCharger(value)
    setFieldValue("no_of_connector", value)
    setFieldValue("connector_type", "")
    setFieldValue("left_connector_type", "")
    setFieldValue("right_connector_type", "")
  }

  useEffect(() => {
    fetchConnectorData()
  }, [])

  return (
    <>
      <ModalComponent
        show={show}
        modalsize={"lg"}
        animationFade={true}
        backdrop="static"
        onHide={() => {
          handleClose()
          resetForm()
        }}
        modeltitle={isUpdate ? "Update charger" : "Add charger"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext={"Cancel"}
        handleSubmit={formik.handleSubmit}
        children={
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12} className="mb-3">
                <FloatingLabel
                  controlId="sn_number"
                  label={
                    <>
                      SN Number<span className="required_star">*</span>
                    </>
                  }
                  className="mb-1"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="sn_number"
                    autoComplete="false"
                    value={values.sn_number}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.sn_number && errors.sn_number}
                    isValid={touched.sn_number && !errors.sn_number}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.sn_number}
                  </Form.Control.Feedback>
                </FloatingLabel>
                {values?.sn_number && (
                  <div className="d-flex align-items-center">
                    <p className="fs_12 m-0 me-3">
                      {process.env.REACT_APP_OCPP_URL + "/" + values?.sn_number}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        const url =
                          process.env.REACT_APP_OCPP_URL +
                          "/" +
                          values?.sn_number
                        navigator.clipboard
                          .writeText(url)
                          .then(() => setCopied(true))
                          .catch(() => setCopied(false))
                      }}
                      className={`btn btn-sm fs_12 ${
                        copied ? "btn-success" : "btn-primary"
                      }`}
                      onMouseLeave={() => setCopied(false)}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                )}
              </Col>

              <Col xs={6}>
                <FloatingLabel
                  controlId="model_name"
                  label={
                    <>
                      Model Name<span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="model_name"
                    autoComplete="false"
                    maxLength={30}
                    value={values.model_name}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.model_name && errors.model_name}
                    isValid={touched.model_name && !errors.model_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.model_name}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col xs={6}>
                <FloatingLabel
                  controlId="vendor"
                  label={
                    <>
                      Vendor<span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="vendor"
                    autoComplete="false"
                    value={values?.vendor}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched?.vendor && errors?.vendor}
                    isValid={touched?.vendor && !errors?.vendor}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.vendor}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col xs={6}>
                <FloatingLabel
                  controlId="firmware_version"
                  label={
                    <>
                      Firmware Version<span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="firmware_version"
                    autoComplete="false"
                    maxLength={30}
                    value={values?.firmware_version}
                    onChange={(e) =>
                      handleOnchange(e, _firmwareVersionRegex_, formik)
                    }
                    onBlur={(e) => handleOnBlur(e, formik, true)}
                    isInvalid={
                      touched?.firmware_version && errors?.firmware_version
                    }
                    isValid={
                      touched?.firmware_version && !errors?.firmware_version
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.firmware_version}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col xs={12} className="mb-3">
                <label className="d-block">
                  Guns Count <span className="required_star">*</span>
                </label>
                <ButtonGroup>
                  <div className="d-flex gap-2">
                    <ToggleButton
                      id="single"
                      type="radio"
                      value="single"
                      name="radio"
                      checked={values?.no_of_connector === "single"}
                      onChange={() => handleSelect("single")}
                      variant={
                        values?.no_of_connector === "single"
                          ? "primary"
                          : "outline-primary"
                      }
                    >
                      Single
                    </ToggleButton>
                    <ToggleButton
                      id="double"
                      type="radio"
                      value="double"
                      name="radio"
                      checked={values?.no_of_connector === "double"}
                      onChange={() => handleSelect("double")}
                      variant={
                        values?.no_of_connector === "double"
                          ? "primary"
                          : "outline-primary"
                      }
                    >
                      Double
                    </ToggleButton>
                  </div>
                </ButtonGroup>
              </Col>

              {values?.no_of_connector === "single" ? (
                <>
                  <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Connector Type<span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={connectorData}
                      value={values?.connector_type}
                      handleSelectChange={(selectconnector) => {
                        setFieldValue("connector_type", selectconnector)
                      }}
                      isMulti={false}
                      placeholder={"Select Connector"}
                    />
                    {touched?.connector_type && errors?.connector_type && (
                      <p className="invalid_error">{errors?.connector_type}</p>
                    )}
                  </Col>
                  {/* <Col xs={6} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Gun Status<span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={selectGunStatus}
                      value={values?.gun_status}
                      handleSelectChange={(selectGunStatus) => {
                        setFieldValue("gun_status", selectGunStatus)
                      }}
                      isMulti={false}
                      placeholder={"Select Status"}
                    />

                    {touched?.gun_status && errors?.gun_status && (
                      <p className="invalid_error">{errors?.gun_status}</p>
                    )}
                  </Col> */}
                </>
              ) : null}

              {values.no_of_connector === "double" ? (
                <>
                  <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Left Connector Type
                      <span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={connectorData}
                      value={values?.left_connector_type}
                      handleSelectChange={(selectconnector) => {
                        setFieldValue("left_connector_type", selectconnector)
                      }}
                      isMulti={false}
                      placeholder={"Select Connector"}
                    />
                    {touched?.left_connector_type &&
                      errors?.left_connector_type && (
                        <p className="invalid_error">
                          {errors?.left_connector_type}
                        </p>
                      )}
                  </Col>
                  {/* <Col xs={6} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Left Gun Status<span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={selectGunStatus}
                      value={values?.left_gun_status}
                      handleSelectChange={(selectGunStatus) => {
                        setFieldValue("left_gun_status", selectGunStatus)
                      }}
                      isMulti={false}
                      placeholder={"Select Status"}
                    />
                    {touched?.left_gun_status && errors?.left_gun_status && (
                      <p className="invalid_error">{errors?.left_gun_status}</p>
                    )}
                  </Col> */}

                  <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Right Connector Type
                      <span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={connectorData}
                      value={values?.right_connector_type}
                      handleSelectChange={(selectconnector) => {
                        setFieldValue("right_connector_type", selectconnector)
                      }}
                      isMulti={false}
                      placeholder={"Select Connector"}
                    />
                    {touched?.right_connector_type &&
                      errors?.right_connector_type && (
                        <p className="invalid_error">
                          {errors?.right_connector_type}
                        </p>
                      )}
                  </Col>
                  {/* <Col xs={6} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Right Gun Status<span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={selectGunStatus}
                      value={values?.right_gun_status}
                      handleSelectChange={(selectGunStatus) => {
                        setFieldValue("right_gun_status", selectGunStatus)
                      }}
                      isMulti={false}
                      placeholder={"Select Status"}
                    />
                    {touched?.right_gun_status && errors?.right_gun_status && (
                      <p className="invalid_error">
                        {errors?.right_gun_status}
                      </p>
                    )}
                  </Col> */}
                </>
              ) : null}

              {/* {values?.no_of_connector === "socket" ? (
                <>
                  <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                    <label>
                      Gun Status<span className="required_star">*</span>
                    </label>
                    <CustomSelect
                      options={selectGunStatus}
                      handleSelectChange={(selectGunStatus) => {
                        setSelectedSingleconnector(
                          "gun_status",
                          selectGunStatus.value
                        )
                      }}
                      isMulti={false}
                      placeholder={"Select Status"}
                    />
                    {touched?.gun_status && errors?.gun_status && (
                      <p className="invalid_error">{errors?.gun_status}</p>
                    )}
                  </Col>
                </>
              ) : null} */}

              <Col xs={12}>
                <FloatingLabel
                  controlId="power_rating"
                  label={
                    <>
                      Rated Power<span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="power_rating"
                    autoComplete="false"
                    maxLength={30}
                    value={values?.power_rating}
                    onChange={(e) => handleOnchange(e, _numberRegex_, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched?.power_rating && errors?.power_rating}
                    isValid={touched?.power_rating && !errors?.power_rating}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.power_rating}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <Row className="align-items-center">
                  <label>Output Current Range(Amp)</label>
                  <Col xs={5}>
                    <FloatingLabel
                      controlId="min_output_current"
                      label={
                        <>
                          Min.<span className="required_star">*</span>
                        </>
                      }
                    >
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="min_output_current"
                        autoComplete="false"
                        maxLength={20}
                        value={values?.min_output_current}
                        onChange={(e) =>
                          handleOnchange(e, _numberRegex_, formik)
                        }
                        onBlur={(e) => handleOnBlur(e, formik)}
                        isInvalid={
                          touched?.min_output_current &&
                          errors?.min_output_current
                        }
                        isValid={
                          touched?.min_output_current &&
                          !errors?.min_output_voltage
                        }
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.min_output_current}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                  <Col xs={1}>-</Col>
                  <Col xs={5}>
                    <FloatingLabel
                      controlId="max_output_current"
                      label={
                        <>
                          Max.<span className="required_star">*</span>
                        </>
                      }
                    >
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="max_output_current"
                        autoComplete="false"
                        maxLength={20}
                        value={values?.max_output_current}
                        onChange={(e) =>
                          handleOnchange(e, _numberRegex_, formik)
                        }
                        onBlur={(e) => handleOnBlur(e, formik)}
                        isInvalid={
                          touched?.max_output_current &&
                          errors?.max_output_current
                        }
                        isValid={
                          touched?.max_output_current &&
                          errors?.max_output_current
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.max_output_current}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>
              </Col>
              <Col xs={6}>
                <Row className="align-items-center">
                  <label>Output Voltage Range DC(Voltage)</label>
                  <Col xs={5}>
                    <FloatingLabel
                      controlId="min_output_voltage"
                      label={
                        <>
                          Min.<span className="required_star">*</span>
                        </>
                      }
                    >
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="min_output_voltage"
                        autoComplete="false"
                        maxLength={20}
                        value={values?.min_output_voltage}
                        onChange={(e) =>
                          handleOnchange(e, _numberRegex_, formik)
                        }
                        onBlur={(e) => handleOnBlur(e, formik)}
                        isInvalid={
                          touched?.min_output_voltage &&
                          errors?.min_output_voltage
                        }
                        isValid={
                          touched?.min_output_voltage &&
                          errors?.min_output_voltage
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.min_output_voltage}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                  <Col xs={1}>-</Col>
                  <Col xs={5}>
                    <FloatingLabel
                      controlId="max_output_voltage"
                      label={
                        <>
                          Max.<span className="required_star">*</span>
                        </>
                      }
                    >
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="max_output_voltage"
                        autoComplete="false"
                        maxLength={20}
                        value={values?.max_output_voltage}
                        onChange={(e) =>
                          handleOnchange(e, _numberRegex_, formik)
                        }
                        onBlur={(e) => handleOnBlur(e, formik)}
                        isInvalid={
                          touched?.max_output_voltage &&
                          errors?.max_output_voltage
                        }
                        isValid={
                          touched?.max_output_voltage &&
                          errors?.max_output_voltage
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors?.max_output_voltage}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        }
      />
    </>
  )
}

export default AddEditCharger
